import * as Sentry from '@sentry/react'
import { fetchAnnounceByUserId } from './announces'
import { fetchWorkByUserId } from './announcesWorks'
import { fetchEventByUserId } from './announcesEvents'
import { fetchEstablishmentByUserId } from './announcesEstablishment'
import { fetchAnnounceLibertinByUserId } from './announcesLibertins'

interface StatItem {
  id: number
  title: string
  views: number
  type: 'announce' | 'work' | 'event' | 'establishment' | 'libertin'
  createdAt: string
  updatedAt: string
}

export interface StatisticsResponse {
  items: StatItem[]
  totalViews: number
}

export const fetchAllUserStatistics = async (
  userId: string,
  token: string,
): Promise<StatisticsResponse> => {
  try {
    const results = await Promise.allSettled([
      fetchAnnounceByUserId(userId, token),
      fetchWorkByUserId(userId, token),
      fetchEventByUserId(userId, token),
      fetchEstablishmentByUserId(userId, token),
      fetchAnnounceLibertinByUserId(userId, token),
    ])

    const items: StatItem[] = []

    const announceTypes = [
      { type: 'announce', defaultTitle: 'Annonce sans titre' },
      { type: 'work', defaultTitle: "Offre d'emploi sans titre" },
      { type: 'event', defaultTitle: 'Événement sans titre' },
      { type: 'establishment', defaultTitle: 'Établissement sans titre' },
      { type: 'libertin', defaultTitle: 'Annonce libertin sans titre' },
    ]

    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value) {
        const item = result.value
        const { type, defaultTitle } = announceTypes[index]

        items.push({
          id: item.id,
          title: item.title || defaultTitle,
          views: item.views || 0,
          type: type as StatItem['type'],
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
        })
      } else if (result.status === 'rejected') {
        Sentry.captureException(result.reason, {
          extra: {
            action: `fetch${announceTypes[index].type}Statistics`,
            userId,
            timestamp: new Date().toISOString(),
          },
          tags: {
            type: 'api_error',
            method: `fetch${announceTypes[index].type}Statistics`,
          },
        })
      }
    })

    const totalViews = items.reduce((sum, item) => sum + (item.views || 0), 0)

    return {
      items,
      totalViews,
    }
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'fetchAllUserStatistics',
        userId,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'fetchAllUserStatistics',
      },
    })

    return {
      items: [],
      totalViews: 0,
    }
  }
}

export const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'formatDate',
        dateString,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'format_error',
        method: 'formatDate',
      },
    })
    return 'Date invalide'
  }
}

export const getTypeLabel = (type: string): string => {
  const types = {
    announce: 'Annonce',
    work: "Offre d'emploi",
    event: 'Événement',
    establishment: 'Établissement',
    libertin: 'Annonce libertin',
  }
  return types[type as keyof typeof types] || type
}
